import { useState, useCallback } from "react";
import { httpRequest } from "../utils/httpClient";
import { API_BASE } from "../config/api";
import { authService } from "../services/auth";
import { isOwnedStatus } from "../utils/gameOwnership";
import logger from '../utils/logger';

export function useSimplifyCollection(navigate, showBanner) {
  const [collection, setCollection] = useState([]);
  const [hasOwnedGames, setHasOwnedGames] = useState(false);
  const [ownedCount, setOwnedCount] = useState(0);

  // Lightweight check: only fetches owned count to enable the button quickly.
  // Does NOT load the full collection payload.
  // NOTE: we do NOT guard on authService.getToken() because authentication may
  // be carried by an httpOnly cookie; the backend will reject unauthenticated
  // requests and we handle the 401 below.
  const checkHasOwnedGames = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/profile/collection/count`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        const count = data.owned_count || 0;
        setOwnedCount(count);
        setHasOwnedGames(count > 0);
      }
    } catch (err) {
      logger.error("Failed to check collection count:", err);
    }
  }, []);

  // Load user's collection (used by addToCollection after a mutation)
  // NOTE: we do NOT guard on authService.getToken() because authentication may
  // be carried by an httpOnly cookie; the backend will reject unauthenticated
  // requests and we handle the 401 below.
  const loadCollection = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        // API may return array directly or {games: [...]}; handle both
        const games = Array.isArray(data) ? data : (data.games || []);
        const normalizedGames = games.map(g => ({
          ...g,
          id: g.id || g.game_id
        }));
        setCollection(normalizedGames);
        setHasOwnedGames(normalizedGames.some(g => isOwnedStatus(g.ownership_status)));
        // ownedCount stays driven by the lightweight count endpoint so the Jet Set Go
        // button label remains consistent with the dedicated owned-game count.
      }
    } catch (err) {
      logger.error("Failed to load collection:", err);
    }
  }, []);

  // Check if game is in collection (check both id and game_id fields)
  const isInCollection = useCallback((gameId) => {
    if (!Array.isArray(collection) || !gameId) return false;
    const target = String(gameId);
    return collection.some((g) => String(g.id) === target || String(g.game_id) === target);
  }, [collection]);

  // Add game to collection
  const addToCollection = useCallback(async (game) => {
    const token = authService.getToken();
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const gameId = game.id || game.game_id;
      logger.debug("[useSimplifyCollection] Adding to collection:", { game, extractedGameId: gameId });
      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
        },
        body: JSON.stringify({ game_id: gameId }),
      });

      if (res.ok) {
        await loadCollection();
        showBanner('Game added to library', 'success');
      } else {
        showBanner('Failed to add game to library', 'error');
      }
    } catch (err) {
      logger.error("Failed to add game to library:", err);
      showBanner('Failed to add game to library', 'error');
    }
  }, [loadCollection, showBanner, navigate]);

  return {
    collection,
    setCollection,
    hasOwnedGames,
    ownedCount,
    checkHasOwnedGames,
    loadCollection,
    isInCollection,
    addToCollection
  };
}
